// Twitter Integration Factory
// Factory class for creating and managing Twitter integration instances

import { TwitterIntegration } from './TwitterIntegration';
import { IntegrationConfig, EncryptedCredentials, IntegrationSettings } from '../../../types/integration';

export interface TwitterCredentials {
  bearerToken: string;
  apiKey: string;
  apiSecret: string;
  accessToken: string;
  accessTokenSecret: string;
  clientId?: string;
  clientSecret?: string;
}

export interface TwitterSettings extends IntegrationSettings {
  enableTweeting: boolean;
  enableAnalytics: boolean;
  enableThreads: boolean;
  tweetSettings: {
    maxTweetLength: number;
    autoSplitLongContent: boolean;
    addThreadNumbers: boolean;
    includeMediaInThreads: boolean;
  };
  contentSettings: {
    maxHashtags: number;
    autoAddHashtags: boolean;
    mentionNotifications: boolean;
    linkShortening: boolean;
  };
  rateLimitSettings: {
    tweetsPerHour: number;
    respectRateLimits: boolean;
    backoffOnLimit: boolean;
  };
}

export interface TwitterConfig extends IntegrationConfig {
  settings: TwitterSettings;
}

export class TwitterIntegrationFactory {
  private static instances: Map<string, TwitterIntegration> = new Map();

  /**
   * Create a new Twitter integration instance
   */
  static async create(config: TwitterConfig): Promise<TwitterIntegration> {
    try {
      // Validate configuration
      this.validateConfig(config);

      // Create integration instance
      const integration = new TwitterIntegration(config);

      // Test connection
      const connectionTest = await integration.testConnection();
      if (!connectionTest.success) {
        throw new Error(`Connection test failed: ${connectionTest.message}`);
      }

      // Connect to Twitter
      await integration.connect();

      // Store instance
      this.instances.set(config.id, integration);

      return integration;
    } catch (error) {
      throw new Error(`Failed to create Twitter integration: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get existing integration instance
   */
  static getInstance(integrationId: string): TwitterIntegration | null {
    return this.instances.get(integrationId) || null;
  }

  /**
   * Remove integration instance
   */
  static async removeInstance(integrationId: string): Promise<void> {
    const integration = this.instances.get(integrationId);
    if (integration) {
      await integration.disconnect();
      this.instances.delete(integrationId);
    }
  }

  /**
   * List all active integration instances
   */
  static getActiveInstances(): TwitterIntegration[] {
    return Array.from(this.instances.values());
  }

  /**
   * Update integration configuration
   */
  static async updateConfig(integrationId: string, newConfig: Partial<TwitterConfig>): Promise<TwitterIntegration> {
    const existingIntegration = this.instances.get(integrationId);
    if (!existingIntegration) {
      throw new Error(`Integration ${integrationId} not found`);
    }

    // Disconnect existing integration
    await existingIntegration.disconnect();

    // Create new configuration
    const updatedConfig: TwitterConfig = {
      ...existingIntegration.getConfig(),
      ...newConfig
    } as TwitterConfig;

    // Validate new configuration
    this.validateConfig(updatedConfig);

    // Create new integration instance
    const newIntegration = new TwitterIntegration(updatedConfig);
    
    // Test and connect
    const connectionTest = await newIntegration.testConnection();
    if (!connectionTest.success) {
      throw new Error(`Connection test failed: ${connectionTest.message}`);
    }

    await newIntegration.connect();

    // Replace instance
    this.instances.set(integrationId, newIntegration);

    return newIntegration;
  }

  /**
   * Create integration from setup wizard data
   */
  static async createFromWizard(wizardData: {
    name: string;
    bearerToken?: string;
    apiKey: string;
    apiSecret: string;
    accessToken?: string;
    accessTokenSecret?: string;
    enableTweeting: boolean;
    enableAnalytics: boolean;
    enableThreads: boolean;
    maxTweetsPerHour: number;
    autoSplitLongContent: boolean;
  }): Promise<TwitterIntegration> {
    // Create encrypted credentials (placeholder - in real implementation would encrypt)
    const encryptedCredentials: EncryptedCredentials = {
      encrypted: JSON.stringify({
        bearerToken: wizardData.bearerToken,
        apiKey: wizardData.apiKey,
        apiSecret: wizardData.apiSecret,
        accessToken: wizardData.accessToken,
        accessTokenSecret: wizardData.accessTokenSecret
      }),
      keyId: 'default-key',
      algorithm: 'AES-256-GCM',
      createdAt: new Date()
    };

    const config: TwitterConfig = {
      id: this.generateId(),
      name: wizardData.name,
      type: 'social',
      platform: 'twitter',
      status: 'active',
      credentials: encryptedCredentials,
      settings: {
        enabled: true,
        autoSync: true,
        batchSize: 100,
        rateLimitPerMinute: 300, // Twitter API v2 rate limits
        retryAttempts: 3,
        timeoutMs: 30000,
        customSettings: {},
        enableTweeting: wizardData.enableTweeting,
        enableAnalytics: wizardData.enableAnalytics,
        enableThreads: wizardData.enableThreads,
        tweetSettings: {
          maxTweetLength: 280,
          autoSplitLongContent: wizardData.autoSplitLongContent,
          addThreadNumbers: true,
          includeMediaInThreads: false // Twitter limitation
        },
        contentSettings: {
          maxHashtags: 10, // Twitter allows more hashtags than LinkedIn
          autoAddHashtags: false,
          mentionNotifications: true,
          linkShortening: true
        },
        rateLimitSettings: {
          tweetsPerHour: wizardData.maxTweetsPerHour,
          respectRateLimits: true,
          backoffOnLimit: true
        }
      },
      fieldMappings: [],
      syncSchedule: {
        enabled: true,
        frequency: 'real_time',
        timezone: 'UTC',
        batchSize: 100,
        conflictResolution: 'auto_source_wins'
      },
      createdAt: new Date(),
      updatedAt: new Date(),
      healthStatus: {
        status: 'healthy',
        lastCheck: new Date(),
        responseTime: 0,
        errorRate: 0,
        uptime: 100,
        issues: []
      },
      metadata: {
        tags: ['social', 'twitter', 'microblogging'],
        category: 'social_media',
        version: '1.0.0',
        supportedFeatures: ['post_publishing', 'analytics', 'media_upload', 'thread_posting'],
        limitations: ['no_post_editing', 'no_native_scheduling', 'limited_audience_targeting'],
        customData: {}
      }
    };

    return await this.create(config);
  }

  /**
   * Test Twitter credentials
   */
  static async testCredentials(credentials: {
    bearerToken?: string;
    apiKey: string;
    apiSecret: string;
    accessToken?: string;
    accessTokenSecret?: string;
  }): Promise<{
    success: boolean;
    message: string;
    userProfile?: {
      id: string;
      username: string;
      name: string;
      verified: boolean;
      followers: number;
      profileImage?: string;
    };
    capabilities?: string[];
  }> {
    try {
      // Create encrypted credentials for testing
      const encryptedCredentials: EncryptedCredentials = {
        encrypted: JSON.stringify(credentials),
        keyId: 'test-key',
        algorithm: 'AES-256-GCM',
        createdAt: new Date()
      };

      // Create temporary integration for testing
      const tempConfig: TwitterConfig = {
        id: 'temp-test',
        name: 'Test Integration',
        type: 'social',
        platform: 'twitter',
        status: 'configuring',
        credentials: encryptedCredentials,
        settings: {
          enabled: true,
          autoSync: false,
          batchSize: 100,
          rateLimitPerMinute: 300,
          retryAttempts: 3,
          timeoutMs: 30000,
          customSettings: {},
          enableTweeting: false,
          enableAnalytics: false,
          enableThreads: false,
          tweetSettings: {
            maxTweetLength: 280,
            autoSplitLongContent: false,
            addThreadNumbers: false,
            includeMediaInThreads: false
          },
          contentSettings: {
            maxHashtags: 10,
            autoAddHashtags: false,
            mentionNotifications: false,
            linkShortening: false
          },
          rateLimitSettings: {
            tweetsPerHour: 50,
            respectRateLimits: true,
            backoffOnLimit: true
          }
        },
        fieldMappings: [],
        syncSchedule: {
          enabled: false,
          frequency: 'daily',
          timezone: 'UTC',
          batchSize: 100,
          conflictResolution: 'auto_source_wins'
        },
        createdAt: new Date(),
        updatedAt: new Date(),
        healthStatus: {
          status: 'healthy',
          lastCheck: new Date(),
          responseTime: 0,
          errorRate: 0,
          uptime: 100,
          issues: []
        },
        metadata: {
          tags: ['test'],
          category: 'social_media',
          version: '1.0.0',
          supportedFeatures: ['test'],
          limitations: [],
          customData: {}
        }
      };

      const tempIntegration = new TwitterIntegration(tempConfig);
      
      // Test connection
      const connectionResult = await tempIntegration.testConnection();
      if (!connectionResult.success) {
        return connectionResult;
      }

      // Get user profile
      await tempIntegration.connect();
      const platforms = await tempIntegration.getConnectedPlatforms();
      
      const userProfile = platforms.length > 0 ? {
        id: platforms[0].accountId,
        username: platforms[0].accountName,
        name: connectionResult.details?.name || platforms[0].accountName,
        verified: false, // Would be determined from actual API response
        followers: 0, // Would be populated from actual API
        profileImage: undefined
      } : undefined;

      // Determine capabilities based on credentials
      const capabilities: string[] = ['read_tweets'];
      
      if (credentials.accessToken && credentials.accessTokenSecret) {
        capabilities.push('write_tweets', 'upload_media', 'delete_tweets');
      }
      
      if (credentials.bearerToken) {
        capabilities.push('read_analytics', 'read_user_data');
      }

      return {
        success: true,
        message: 'Credentials valid',
        userProfile,
        capabilities
      };
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Credential test failed'
      };
    }
  }

  /**
   * Get integration health status
   */
  static async getHealthStatus(integrationId: string): Promise<{
    status: 'healthy' | 'warning' | 'error';
    issues: string[];
    lastCheck: Date;
    metrics: {
      tweetsPublished: number;
      engagementRate: number;
      followerGrowth: number;
      rateLimitStatus: {
        remaining: number;
        resetTime: Date;
      };
      lastSyncTime?: Date;
    };
  }> {
    const integration = this.instances.get(integrationId);
    if (!integration) {
      return {
        status: 'error',
        issues: ['Integration not found'],
        lastCheck: new Date(),
        metrics: {
          tweetsPublished: 0,
          engagementRate: 0,
          followerGrowth: 0,
          rateLimitStatus: {
            remaining: 0,
            resetTime: new Date()
          }
        }
      };
    }

    try {
      // Test connection
      const connectionTest = await integration.testConnection();
      const issues: string[] = [];
      
      if (!connectionTest.success) {
        issues.push(`Connection failed: ${connectionTest.message}`);
      }

      // Check platform connections
      const platforms = await integration.getConnectedPlatforms();
      const disconnectedPlatforms = platforms.filter(p => !p.connected);
      
      if (disconnectedPlatforms.length > 0) {
        issues.push(`Disconnected platforms: ${disconnectedPlatforms.map(p => p.displayName).join(', ')}`);
      }

      // Check for rate limiting issues
      if (connectionTest.duration && connectionTest.duration > 3000) {
        issues.push('API response times are slow - possible rate limiting');
      }

      // Get metrics (placeholder implementation)
      const metrics = {
        tweetsPublished: 0, // Would get from analytics
        engagementRate: 0, // Would calculate from recent tweets
        followerGrowth: 0, // Would track over time
        rateLimitStatus: {
          remaining: 300, // Would get from API headers
          resetTime: new Date(Date.now() + 15 * 60 * 1000) // 15 minutes from now
        },
        lastSyncTime: integration.getLastSyncTime()
      };

      const status = issues.length === 0 ? 'healthy' : 
                    issues.some(issue => issue.includes('Connection failed')) ? 'error' : 'warning';

      return {
        status,
        issues,
        lastCheck: new Date(),
        metrics
      };
    } catch (error) {
      return {
        status: 'error',
        issues: [`Health check failed: ${error instanceof Error ? error.message : 'Unknown error'}`],
        lastCheck: new Date(),
        metrics: {
          tweetsPublished: 0,
          engagementRate: 0,
          followerGrowth: 0,
          rateLimitStatus: {
            remaining: 0,
            resetTime: new Date()
          }
        }
      };
    }
  }

  /**
   * Split long content into Twitter thread
   */
  static splitContentForThread(content: string, maxLength: number = 280, addNumbers: boolean = true): string[] {
    if (content.length <= maxLength) {
      return [content];
    }

    const tweets: string[] = [];
    const words = content.split(' ');
    let currentTweet = '';
    let tweetIndex = 1;

    for (const word of words) {
      const numberPrefix = addNumbers ? `${tweetIndex}/ ` : '';
      const testTweet = currentTweet ? `${currentTweet} ${word}` : word;
      
      if ((numberPrefix + testTweet).length <= maxLength) {
        currentTweet = testTweet;
      } else {
        if (currentTweet) {
          tweets.push(addNumbers ? `${tweetIndex}/ ${currentTweet}` : currentTweet);
          tweetIndex++;
          currentTweet = word;
        } else {
          // Single word is too long, split it
          const chunks = this.splitLongWord(word, maxLength - numberPrefix.length);
          for (let i = 0; i < chunks.length; i++) {
            tweets.push(addNumbers ? `${tweetIndex}/ ${chunks[i]}` : chunks[i]);
            tweetIndex++;
          }
          currentTweet = '';
        }
      }
    }

    if (currentTweet) {
      tweets.push(addNumbers ? `${tweetIndex}/ ${currentTweet}` : currentTweet);
    }

    return tweets;
  }

  /**
   * Validate integration configuration
   */
  private static validateConfig(config: TwitterConfig): void {
    // Decrypt credentials for validation (in real implementation would properly decrypt)
    let credentials: TwitterCredentials;
    try {
      credentials = JSON.parse(config.credentials.encrypted) as TwitterCredentials;
    } catch (error) {
      throw new Error('Invalid encrypted credentials format');
    }

    if (!credentials.apiKey) {
      throw new Error('API Key is required');
    }

    if (!credentials.apiSecret) {
      throw new Error('API Secret is required');
    }

    if (!credentials.bearerToken && !credentials.accessToken) {
      throw new Error('Either Bearer Token or OAuth tokens (accessToken + accessTokenSecret) are required');
    }

    if (credentials.accessToken && !credentials.accessTokenSecret) {
      throw new Error('Access Token Secret is required when using OAuth 1.0a');
    }

    if (!config.settings.enableTweeting && !config.settings.enableAnalytics) {
      throw new Error('At least one feature (tweeting or analytics) must be enabled');
    }

    // Validate rate limit settings
    if (config.settings.rateLimitSettings.tweetsPerHour > 300) {
      throw new Error('Tweets per hour cannot exceed 300 (Twitter API limit)');
    }

    // Validate tweet length
    if (config.settings.tweetSettings.maxTweetLength > 280) {
      throw new Error('Maximum tweet length cannot exceed 280 characters');
    }
  }

  /**
   * Split a long word that exceeds the character limit
   */
  private static splitLongWord(word: string, maxLength: number): string[] {
    const chunks: string[] = [];
    for (let i = 0; i < word.length; i += maxLength) {
      chunks.push(word.slice(i, i + maxLength));
    }
    return chunks;
  }

  /**
   * Generate unique integration ID
   */
  private static generateId(): string {
    return `twitter_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}